import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 7 geometry — the last of the measurement work before proof.
 *
 * Angles gain relationships, so a figure can be reasoned about rather than
 * only measured. The circle brings the first constant a student meets that is
 * not a fraction. Scale drawings put proportion back into a picture, and are
 * where a student first meets the fact that area does not scale like length.
 * Surface area is the counterpart to volume, and exists mostly to force the
 * distinction between covering a solid and filling it.
 */
export const GEOMETRY_UPPER_LESSONS: Lesson[] = [
  lesson({
    id: 'angles-01',
    unitId: 'unit-angles-circles',
    conceptId: 'angles',
    title: 'Angles and Lines',
    summary: 'Measuring turn — and the relationships that let you find an angle you were never told.',
    objectives: [
      'Name angles as acute, right, obtuse, straight or reflex',
      'Use complementary, supplementary and vertically opposite angles',
      'Find a missing angle in a triangle or around a point',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Turn, not length',
        blocks: [
          text('An angle does not measure how long the two lines are. It measures how far you would have to **turn** to get from one to the other, which is why two arms of different lengths can make exactly the same angle.'),
          text('A full turn is 360°, and every fact in this lesson comes from dividing that up:'),
          table(['Name', 'Size', 'Where you see it'], [
            ['Acute', 'less than 90°', 'a slice of pizza'],
            ['Right', 'exactly 90°', 'the corner of a page'],
            ['Obtuse', 'between 90° and 180°', 'a reclining chair'],
            ['Straight', 'exactly 180°', 'a flat line'],
            ['Reflex', 'between 180° and 360°', 'the outside of a corner'],
          ]),
        ],
      },
      intuition: {
        title: 'Finding what you were not told',
        blocks: [
          text('The useful part of this topic is not measuring. It is working out an angle nobody gave you, from the ones they did.'),
          list([
            'Angles on a straight line add to 180°.',
            'Angles around a point add to 360°.',
            'The angles inside any triangle add to 180°.',
            'Vertically opposite angles — the pair facing each other where two lines cross — are equal.',
          ]),
          insight('Two of these have names worth keeping straight. **Complementary** angles add to 90°; **supplementary** angles add to 180°. C comes before S in the alphabet, and 90 comes before 180.'),
          warning('Mixing those two up is the most common error here, and it produces an answer that looks entirely reasonable. Before subtracting, look at the picture and ask whether the angles make a corner or a straight line.', 'Complementary or supplementary?'),
          text('The triangle rule is worth believing rather than memorising. Tear the three corners off a paper triangle and lay them next to each other: they always form a straight line. Any triangle, every time.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'these three angles add to 180°' } },
            'Whatever shape the triangle, its three interior angles total a straight line.',
          ),
          text('When two straight lines cross they make four angles. The ones opposite each other are equal, and the ones next to each other are supplementary — so knowing any one of the four gives you all four.'),
          math('a + b = 180° \\qquad a = c \\qquad b = d'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('In a triangle, one angle is 90° and another is 34°. Find the third, and then find its supplement.'),
          steps([
            { label: 'The three angles add to 180°.', latex: '90 + 34 + x = 180' },
            { label: 'Subtract what you know.', latex: 'x = 180 - 124 = 56°' },
            { label: 'A supplement makes a straight line, so subtract from 180.', latex: '180 - 56 = 124°' },
          ]),
          tip('Write the total down before you start — 90, 180 or 360 — and the rest is subtraction. Nearly every wrong answer in this topic starts with the wrong total.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('angles', 4)] },
      independent: { title: 'On your own', blocks: [practice('angles', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('angles', 3, 'Several angles meeting at once.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An angle measures turn, not length.',
            'Complementary angles add to 90°; supplementary angles add to 180°.',
            'A straight line is 180°; a full turn is 360°; a triangle is 180°.',
            'Vertically opposite angles are equal.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'circles-01',
    unitId: 'unit-angles-circles',
    conceptId: 'circles',
    title: 'Circumference and Area of a Circle',
    summary: 'Every circle has the same ratio of circumference to diameter. That ratio is π, and it is not a fraction.',
    objectives: [
      'Use the relationship between radius, diameter and circumference',
      'Calculate circumference with C = πd and area with A = πr²',
      'Work backwards from a circumference or area to a radius',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'A measurement that is the same for every circle',
        blocks: [
          text('Take any circular object — a mug, a coin, a bicycle wheel. Measure the distance round it, and measure the distance across it. Divide the first by the second.'),
          text('You get about 3.14. Not roughly, not usually — *always*, for every circle that has ever been measured, at any size.'),
          math('\\frac{\\text{circumference}}{\\text{diameter}} = \\pi \\approx 3.14159\\ldots'),
          text('That constant is π. It is one of the first genuinely remarkable facts in mathematics, and it is worth pausing on: a wedding ring and the orbit of the Earth share it exactly.'),
        ],
      },
      intuition: {
        title: 'Why the two formulas are different',
        blocks: [
          text('First, the vocabulary, because most errors here are vocabulary errors rather than arithmetic ones.'),
          table(['Word', 'Means', 'Relationship'], [
            ['Radius (r)', 'centre to edge', 'half the diameter'],
            ['Diameter (d)', 'edge to edge through the centre', 'd = 2r'],
            ['Circumference (C)', 'the distance all the way round', 'C = πd = 2πr'],
          ]),
          insight('Circumference is a **length** — once around the edge, measured in cm. Area is a **covering** — the whole inside, measured in cm². Because area is two-dimensional, the radius appears twice: A = πr².'),
          warning('The two formulas are learned as a pair and are therefore constantly swapped. Ask which one the units demand: if the answer should be in cm², the radius has to be used twice.', 'C or A?'),
          text('π is also the first number a student meets that cannot be written as a fraction at all. Its decimal never ends and never repeats. Using 3.14 is a deliberate approximation, and that is why these answers are rounded rather than exact.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'circle', labels: { radius: 'r' } },
            'The radius runs from the centre to the edge. Double it and you have the diameter; multiply that by π and you have the way round.',
          ),
          text('Here is why the area formula has an r² in it. Cut a circle into many thin wedges and lay them alternately point-up and point-down. They form something very close to a rectangle — one whose height is r and whose width is half the circumference, πr.'),
          math('A = \\pi r \\times r = \\pi r^2'),
          text('The thinner the wedges, the closer to a true rectangle it gets. That is a genuine proof hiding in a picture, and it is the first taste of an argument that runs to a limit.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A circular pond has a diameter of 10 m. Find its circumference and its area, using π = 3.14.'),
          steps([
            { label: 'Circumference uses the diameter directly.', latex: 'C = \\pi d = 3.14 \\times 10 = 31.4\\text{ m}' },
            { label: 'Area needs the radius, so halve the diameter first.', latex: 'r = 10 \\div 2 = 5\\text{ m}' },
            { label: 'Square the radius.', latex: '5^2 = 25' },
            { label: 'Multiply by π.', latex: 'A = 3.14 \\times 25 = 78.5\\text{ m}^2' },
          ]),
          tip('The single most common mistake is putting the diameter where the formula asks for r. Write down r = … as its own step, every time, even when it feels unnecessary.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('circles', 4)] },
      independent: { title: 'On your own', blocks: [practice('circles', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('circles', 3, 'Working backwards from a circumference to a radius.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'π is the ratio of circumference to diameter, the same for every circle.',
            'd = 2r — check which one the question gave you.',
            'C = πd is a length; A = πr² is a covering, so the radius is used twice.',
            'π cannot be written as a fraction, so these answers are approximations.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'scale-drawings-01',
    unitId: 'unit-scale-drawings',
    conceptId: 'scale-drawings',
    title: 'Scale Drawings',
    summary: 'Maps, plans and models are proportions drawn out — and lengths, areas and volumes do not scale by the same number.',
    objectives: [
      'Read a scale and convert between drawing and real lengths',
      'Find the scale factor between two similar figures',
      'Work out what happens to an area when every length is scaled',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'A picture that keeps its proportions',
        blocks: [
          text('A floor plan of a kitchen is not the kitchen. It is smaller by a fixed amount — and *fixed* is the whole idea. Every length on the plan is the real length divided by the same number, which is why a plan can be measured with a ruler and trusted.'),
          text('That number is the **scale factor**. A scale of 1 : 50 means one centimetre on paper stands for fifty centimetres in the room.'),
          math('\\text{real length} = \\text{drawing length} \\times 50'),
          text('Everything else in the lesson follows from that one multiplication, including the part that surprises people: what happens to area.'),
        ],
      },
      intuition: {
        title: 'Which way does the multiplication go?',
        blocks: [
          text('The most common mistake is not the arithmetic but the direction. There are only two possibilities, and a single question settles which:'),
          list([
            'Going from the drawing to the real thing, the answer must be **bigger** — so multiply.',
            'Going from the real thing to the drawing, the answer must be **smaller** — so divide.',
          ]),
          warning('A 1 : 200 map, a distance of 6 cm, and an answer of 0.03 cm for the real distance. The arithmetic (6 ÷ 200) is faultless and the answer is a third of a millimetre of countryside. Check the size of the answer before you check the sum.', 'Scaling the wrong way'),
          text('The second idea is less familiar. Enlarge every length by 3, and the area does **not** grow by 3:'),
          table(['Scale factor k', 'Length', 'Area', 'Volume'], [
            ['2', '× 2', '× 4', '× 8'],
            ['3', '× 3', '× 9', '× 27'],
            ['10', '× 10', '× 100', '× 1000'],
            ['k', '× k', '× k²', '× k³'],
          ]),
          insight('Area is a length times a length, so *both* of them get multiplied by k. That is the whole explanation, and it is worth being able to say it rather than remembering the table.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'grid',
              width: 6,
              height: 4,
              shaded: [
                [0, 0],
                [1, 0],
                [2, 0],
                [0, 1],
                [1, 1],
                [2, 1],
              ],
            },
            'The shaded rectangle is 3 by 2. Doubling both sides gives the 6 by 4 grid around it — and four copies of the shaded piece are needed to fill it, not two.',
          ),
          text('Count the small rectangles: two across and two down, so four altogether. Doubling both sides doubles the area twice over.'),
          math('\\text{Area} = 3 \\times 2 = 6 \\qquad (3 \\times 2)(2 \\times 2) = 6 \\times 2 \\times 2 = 24'),
          insight('This is also why a model car at 1 : 43 needs far less than 1/43 of the paint of a real one — paint covers a surface, and surfaces scale by k².'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A garden is drawn to a scale of 1 : 150. On the plan it is 8 cm by 5 cm. What are its real dimensions in metres, and its real area in square metres?'),
          steps([
            { label: 'Scale each length up. The answers must be larger.', latex: '8 \\times 150 = 1200\\text{ cm} \\qquad 5 \\times 150 = 750\\text{ cm}' },
            { label: 'Convert to metres.', latex: '12\\text{ m by }7.5\\text{ m}' },
            { label: 'Find the real area directly from the real lengths.', latex: '12 \\times 7.5 = 90\\text{ m}^2' },
            { label: 'Or from the plan area, remembering the square.', latex: '(8 \\times 5)\\text{ cm}^2 \\times 150^2 = 40 \\times 22500 = 900\\,000\\text{ cm}^2 = 90\\text{ m}^2' },
          ]),
          tip('If the two routes to the area disagree, the second one is usually missing the square. Scaling an area by 150 rather than 150² is off by a factor of 150 — an error too large to be a slip and too tidy to spot without checking.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('scale-drawings', 4)] },
      independent: { title: 'On your own', blocks: [practice('scale-drawings', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('scale-drawings', 3, 'Scaling areas, and working backwards from a real measurement to the scale itself.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A scale is a proportion: every length is multiplied by the same factor.',
            'Drawing to real multiplies; real to drawing divides. Check the answer is the right size.',
            'Lengths scale by k, areas by k², volumes by k³.',
            'Convert units at the end, once, rather than part way through.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'surface-area-01',
    unitId: 'unit-solids',
    conceptId: 'surface-area',
    title: 'Surface Area',
    summary: 'How much skin a solid has, as opposed to how much space it fills.',
    objectives: [
      'Distinguish surface area from volume, including by their units',
      'Find the surface area of a cube and a rectangular prism',
      'Use a net to make sure no face is missed',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Wrapping paper, not stuffing',
        blocks: [
          text('Two questions can be asked about the same box, and they are not the same question.'),
          list([
            '**How much does it hold?** That is volume: length × width × height, measured in cm³.',
            '**How much paper does it take to wrap?** That is surface area: the total area of the faces, measured in cm².',
          ]),
          text('A box 10 cm by 10 cm by 10 cm holds 1000 cm³ and takes 600 cm² of paper. Different questions, different numbers, different units.'),
        ],
      },
      intuition: {
        title: 'Unfold it',
        blocks: [
          text('The reliable way to find surface area is to stop thinking about a solid and start thinking about a flat shape. Cut a box along its edges and flatten it out, and you get a **net** — six rectangles you can measure individually and add.'),
          insight('A rectangular prism has six faces in three matching pairs: front and back, left and right, top and bottom. So find three areas, add them, and double.'),
          math('S = 2(lw + lh + wh)'),
          warning('The faces you cannot see are the ones that get forgotten. Every face you can see has a twin behind it — that is what the 2 in the formula is doing.', 'The missing faces'),
          text('For a cube it is simpler still, because all six faces are the same square:'),
          math('S = 6s^2'),
          insight('The units tell you whether you have answered the right question. Surface area is an area, so it is squared — cm², m². If your answer is in cm³ you have found the volume instead.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'prism', length: 5, width: 3, height: 4, labels: { length: '5 cm', width: '3 cm', height: '4 cm' } },
            'Three faces are visible; three more are hidden behind them, each the same size as the one it faces.',
          ),
          table(['Face', 'Dimensions', 'Area', 'How many'], [
            ['Front and back', '5 × 4', '20 cm²', '2'],
            ['Top and bottom', '5 × 3', '15 cm²', '2'],
            ['Left and right', '3 × 4', '12 cm²', '2'],
            ['Total', '', '2(20 + 15 + 12) = 94 cm²', '6'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A shipping crate is 6 m long, 2 m wide and 3 m high. How much material is needed to cover it completely?'),
          steps([
            { label: 'The three distinct faces.', latex: 'lw = 12,\\quad lh = 18,\\quad wh = 6' },
            { label: 'Add them.', latex: '12 + 18 + 6 = 36' },
            { label: 'Every face has a twin, so double.', latex: 'S = 2 \\times 36 = 72\\text{ m}^2' },
            { label: 'Compare with the volume, which is a different question.', latex: 'V = 6 \\times 2 \\times 3 = 36\\text{ m}^3' },
          ]),
          tip('Those two answers being 72 and 36 is a coincidence of these numbers, not a rule. What is not a coincidence is that one is in m² and the other in m³.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('surface-area', 4)] },
      independent: { title: 'On your own', blocks: [practice('surface-area', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('surface-area', 3, 'Larger solids, where every face has to be accounted for.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Surface area covers a solid; volume fills it.',
            'Surface area is measured in square units, volume in cubic ones.',
            'A rectangular prism has six faces in three pairs: S = 2(lw + lh + wh).',
            'A cube has six identical faces: S = 6s².',
            'Unfold it into a net if you are unsure you have counted them all.',
          ]),
        ],
      },
    },
  }),
];
